import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Award, BadgeCheck, Download, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api.js';
import { downloadCertificadoPdf } from '@/lib/certificado-pdf.js';

export type CertificadoPublico = {
  codigo: string;
  titulo: string;
  emitido_em: string;
  aluno_nome: string;
  programa_titulo: string | null;
  /** Certificação de conselheiro: vale 1 ano. Certificado de curso não vence (null). */
  valido_ate: string | null;
  valido: boolean;
};

export function CertificadoPublicPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const q = useQuery({
    queryKey: ['cert', codigo],
    enabled: !!codigo,
    queryFn: async () =>
      (await api.get<CertificadoPublico>(`/conteudo/certificados/${codigo}`)).data,
  });

  if (q.isLoading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4">
        <p className="text-ha-subtle">Validando certificado…</p>
      </div>
    );
  }

  if (q.error || !q.data) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-md flex-col items-center justify-center px-4 text-center">
        <ShieldAlert className="mb-4 h-12 w-12 text-feedback" />
        <h1 className="text-xl font-bold text-ha-text">Certificado não encontrado</h1>
        <p className="mt-2 text-sm text-ha-muted">
          O código informado não existe ou foi revogado. Verifique o link com quem emitiu o
          certificado.
        </p>
      </div>
    );
  }

  const c = q.data;
  const dataLonga = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="mx-auto max-w-lg px-4 py-12 md:py-16">
      {c.valido ? (
        <div className="rounded-card border border-emerald-600/40 bg-emerald-500/10 px-4 py-3 text-center">
          <div className="mb-1 flex items-center justify-center gap-2 text-emerald-700">
            <BadgeCheck className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">
              {c.valido_ate ? `Certificado válido até ${dataLonga(c.valido_ate)}` : 'Certificado autêntico'}
            </span>
          </div>
          <p className="text-xs text-ha-muted">Validado em {new Date().toLocaleString('pt-BR')}</p>
        </div>
      ) : (
        <div className="rounded-card border-2 border-ha-border2 bg-ha-card2 px-4 py-3 text-center">
          <div className="mb-1 flex items-center justify-center gap-2 text-ha-text">
            <ShieldAlert className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">Certificado vencido em {dataLonga(c.valido_ate!)}</span>
          </div>
          <p className="text-xs text-ha-muted">
            É autêntico, mas não está mais válido: a certificação vale 1 ano e se renova com uma nova prova.
          </p>
        </div>
      )}

      <div className="mt-8 rounded-card border border-ha-border2 bg-ha-card p-8 text-center shadow-lg">
        <p className="mb-2 text-xs uppercase tracking-widest text-ha-muted">Hack Academy</p>
        <Award className="mx-auto mb-4 h-10 w-10 text-accent" />
        <h1 className="mb-2 text-2xl font-black text-accent">Certificado</h1>
        <p className="text-sm text-ha-subtle">Certificamos que</p>
        <p className="mt-2 text-xl font-bold text-ha-text">{c.aluno_nome}</p>
        <p className="mt-4 text-sm text-ha-subtle">{c.valido_ate ? 'foi aprovado na certificação' : 'concluiu com êxito'}</p>
        <p className="mt-2 text-lg font-semibold text-ha-text">{c.titulo}</p>
        {c.programa_titulo ? (
          <p className="mt-2 text-sm text-ha-muted">Programa: {c.programa_titulo}</p>
        ) : null}
        <p className="mt-6 text-sm text-ha-subtle">
          Emitido em{' '}
          {new Date(c.emitido_em).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <p className="mt-6 font-mono text-xs text-ha-muted">Código: {c.codigo}</p>

        <button
          type="button"
          onClick={() =>
            downloadCertificadoPdf({
              titulo: c.titulo,
              aluno_nome: c.aluno_nome,
              emitido_em: c.emitido_em,
              codigo: c.codigo,
              programa_titulo: c.programa_titulo,
              valido_ate: c.valido_ate,
            })
          }
          className="mt-8 inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-bold text-black hover:opacity-90"
        >
          <Download className="h-4 w-4" />
          Baixar PDF
        </button>
      </div>
    </div>
  );
}
